import { CheckOutlined, FileAddOutlined } from '@ant-design/icons';
import { message, Modal, Tooltip } from 'antd';
import CustomStore from 'devextreme/data/custom_store';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CompanyCandidate, FundraisingCandidate, FundraisingLinkAPI, UnlinkedContract } from '../../Api/FundraisingLinkAPI';

export interface FundraisingLinkRow extends UnlinkedContract {
    dcCompanyId?: string;
    dcCompanyName?: string;
    fundraisingId?: string;
    dcName?: string;
    dcVehicle?: string;
    dcCommitment?: number;
}

export const useTableFundraisingLink = (baseURL: string | undefined, clientId: string, moduleRole: string) => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<FundraisingLinkRow[]>([]);
    const [candidates, setCandidates] = useState<FundraisingCandidate[]>([]);
    const companyCandidatesRef = useRef<CompanyCandidate[] | null>(null);
    const api = useMemo(() => FundraisingLinkAPI(baseURL, clientId), [baseURL, clientId]);

    useEffect(() => {
        companyCandidatesRef.current = null;
        Promise.all([api.getUnlinkedContracts(), api.getFundraisingCandidates()])
            .then(([contracts, fundraisingCandidates]) => {
                setRows(contracts
                    .filter(contract => contract.idPersonType !== 2 || Boolean(contract.companyName?.trim()))
                    .map(contract => contract.idPersonType === 2
                        ? { ...contract, name: contract.companyName!.trim() }
                        : contract));
                setCandidates(fundraisingCandidates);
            })
            .finally(() => setLoading(false));
    }, [api]);

    const getCompanyCandidates = useCallback(async () => {
        if (companyCandidatesRef.current === null) {
            companyCandidatesRef.current = await api.getCompanyCandidates();
        }

        return companyCandidatesRef.current;
    }, [api]);

    const companyStore = useMemo(() => new CustomStore({
        key: 'id',
        load: async (loadOptions: { searchValue?: unknown }) => {
            const searchValue = String(loadOptions.searchValue ?? '').trim().toLocaleLowerCase();
            if (searchValue.length < 3) {
                return [];
            }

            return (await getCompanyCandidates())
                .filter(company => company.id.includes(searchValue) || company.name.toLocaleLowerCase().includes(searchValue))
                .slice(0, 50);
        },
        byKey: async (id: string | number) => (await getCompanyCandidates()).find(company => company.id === String(id))
    }), [getCompanyCandidates]);

    const setFundraisingSelection = useCallback((newData: FundraisingLinkRow, fundraisingId: string) => {
        const candidate = candidates.find(item => item.id === fundraisingId);
        newData.fundraisingId = fundraisingId;
        newData.dcName = candidate?.name;
        newData.dcVehicle = candidate?.vehicle;
        newData.dcCommitment = candidate?.commitment;
    }, [candidates]);

    const setCompanySelection = useCallback(function (this: { defaultSetCellValue: (newData: FundraisingLinkRow, value: string) => void }, newData: FundraisingLinkRow, value: string | number | CompanyCandidate) {
        const dcCompanyId = typeof value === 'object' ? value.id : String(value);
        const selectedCompany = companyCandidatesRef.current?.find(company => company.id === dcCompanyId);

        this.defaultSetCellValue(newData, dcCompanyId);
        newData.dcCompanyName = selectedCompany?.name ?? (typeof value === 'object' ? value.name : '');
    }, []);

    const refreshFundraisingCandidates = useCallback(async () => {
        setCandidates(await api.getFundraisingCandidates());
    }, [api]);

    const createFundraisingProcess = useCallback((row: FundraisingLinkRow) => {
        const companyName = row.idPersonType === 2 ? row.companyName?.trim() : row.name.trim();
        const idVehicle = row.idVehicle;
        const selectedCompany = companyCandidatesRef.current?.find(company => company.id === row.dcCompanyId);

        if ((!selectedCompany && !companyName) || !idVehicle) {
            message.error('El contrato no tiene los datos necesarios para crear el Fundraise process.');
            return;
        }

        Modal.confirm({
            title: 'Crear Fundraise process',
            content: `Se creará un Fundraise process para ${selectedCompany?.name ?? companyName}. ¿Quieres continuar?`,
            okText: 'Crear',
            cancelText: 'Cancelar',
            onOk: async () => {
                setLoading(true);
                try {
                    const fundraisingId = await api.createFundraisingProcess({
                        idVehicle,
                        projectedCommitment: row.commitment,
                        estimatedCloseDate: new Date().toISOString(),
                        ...(selectedCompany
                            ? { idCompany: Number(selectedCompany.id) }
                            : { companyName })
                    });

                    try {
                        await api.saveFundraisingProccessId(row.contractId, fundraisingId);
                    } catch {
                        message.error(`El Fundraise process ${fundraisingId} se ha creado, pero no se ha podido vincular al contrato ${row.contractId}.`);
                        return Promise.reject();
                    }

                    await refreshFundraisingCandidates();
                    setRows(currentRows => currentRows.filter(item => item.contractId !== row.contractId));
                    message.success(`Fundraise process ${fundraisingId} creado y vinculado al contrato ${row.contractId}.`);
                } catch {
                    message.error('No se ha podido crear el Fundraise process.');
                    return Promise.reject();
                } finally {
                    setLoading(false);
                }

                return undefined;
            }
        });
    }, [api, refreshFundraisingCandidates]);

    const confirmLink = useCallback(async (row: FundraisingLinkRow) => {
        if (!row.fundraisingId) {
            message.warning('Selecciona un Fundraising ID antes de vincular el contrato.');
            return;
        }

        setLoading(true);
        try {
            await api.saveFundraisingProccessId(row.contractId, row.fundraisingId);
            setRows(currentRows => currentRows.filter(item => item.contractId !== row.contractId));
            message.success(`Fundraise process ${row.fundraisingId} vinculado al contrato ${row.contractId}.`);
        } catch {
            message.error(`No se ha podido vincular el contrato ${row.contractId}.`);
        } finally {
            setLoading(false);
        }
    }, [api]);

    const renderLinkAction = useCallback((cell: any) => (
        <Tooltip title={cell.data.fundraisingId ? 'Vincular contrato' : 'Selecciona un Fundraising ID'}>
            <button
                className="fundraising-link-action"
                type="button"
                disabled={moduleRole === 'Read' || !cell.data.fundraisingId}
                onClick={() => confirmLink(cell.data)}
                aria-label={`Vincular contrato ${cell.data.contractId}`}
            >
                <CheckOutlined />
            </button>
        </Tooltip>
    ), [confirmLink, moduleRole]);

    const renderCreateAction = useCallback((cell: any) => (
        <Tooltip title="Crear">
            <button
                className="fundraising-link-action"
                type="button"
                disabled={moduleRole === 'Read' || !cell.data.idVehicle || Boolean(cell.data.fundraisingId)}
                onClick={() => createFundraisingProcess(cell.data)}
                aria-label={`Crear Fundraise process para el contrato ${cell.data.contractId}`}
            >
                <FileAddOutlined />
            </button>
        </Tooltip>
    ), [createFundraisingProcess, moduleRole]);

    return { loading, rows, candidates, companyStore, setCompanySelection, setFundraisingSelection, renderLinkAction, renderCreateAction };
};
